/* eslint-disable no-unused-vars */
/* eslint-disable no-useless-catch */
/* eslint-disable spaced-comment */
/* eslint-disable no-underscore-dangle, no-console */
/**
 * Extracted utility functions for applications.js
 * These functions are testable and can be mocked independently
 */

// Self-contained MSAL token management constants
// Import for GA tracking
import {
  isMsalTokenExpiredError,
  Auth,
  clearMsalStorage,
  isPublicRoute
} from '@exo/frontend-common-app-shell';
import { trackEvent } from './gaTagManager';
import { TRACKING_EVENTS, isAnalyticsConsented, getErrorContext } from './trackingConfig';

export const MSAL_CACHE = 'auth.msalLastAuthResult';
export const AUTH_TRACE = 'auth.trace.id';

/**
 * Wipe every stale MSAL storage key and redirect to a fresh SSO login.
**/
export const clearMsalStorageAndRedirect = () => {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname || '';
  if (currentPath.startsWith('/auth/employee')) return;
  clearMsalStorage();
  window.location.replace('/auth/employee');
};

export const throwIfUnrecoverable = (error) => {
  if (isMsalTokenExpiredError(error)) {
    const unrecoverableError = new Error(`unrecoverable_token_error: ${error.message || error.error_description}`);
    unrecoverableError.isUnrecoverable = true;
    unrecoverableError.originalErrorCode = error?.errorCode || error?.error || '';
    throw unrecoverableError;
  }
};

export const readMsalCache = () => {
  try {
    if (typeof window === 'undefined') return null;
    const fromSession = window.sessionStorage.getItem(MSAL_CACHE);
    const fromLocal = window.localStorage.getItem(MSAL_CACHE);
    const parse = (raw) => {
      if (!raw) return null;
      try { return JSON.parse(raw); } catch (e) { return null; }
    };
    const session = parse(fromSession);
    const local = parse(fromLocal);
    if (session && local) {
      const sessionExp = Number(session?.expiresOn || session?.value?.expiresOn || 0);
      const localExp = Number(local?.expiresOn || local?.value?.expiresOn || 0);
      return sessionExp >= localExp ? session : local;
    }
    return session || local || null;
  } catch (e) {
    return null;
  }
};
const AUTH_SESSION_HEADERS_READY_TS = 'auth.session.headers.ready.ts';

/**
 * Log frontend error to server via the global Auth instance.
 * Used in non-React utility files that cannot call useLogFeError hook.
 * Falls back silently if Auth instance is not yet available.
 */
export const logFeErrorToServer = (errorInput) => {
  try {
    let userEmail = '';
    try {
      const ctx = JSON.parse(window.localStorage.getItem('sessionContext') || '{}');
      userEmail = ctx.email || ctx.id || '';
    } catch (e) { /* ignore */ }
    const enriched = {
      ...errorInput,
      message: userEmail ? `[user: ${userEmail}] ${errorInput.message}` : errorInput.message
    };
    // eslint-disable-next-line no-underscore-dangle
    const auth = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
    if (auth?.logError?.logErrorToServer) {
      auth.logError.logErrorToServer(enriched);
    }
  } catch (e) {
    // fail silently - logging is a nice-to-have
  }
};

export const getAuthTraceId = () => {
  if (typeof window === 'undefined') {
    return 'server';
  }

  try {
    let traceId = window.sessionStorage.getItem(AUTH_TRACE);
    if (!traceId) {
      traceId = `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      window.sessionStorage.setItem(AUTH_TRACE, traceId);
    }
    return traceId;
  } catch (e) {
    return 'auth-trace-unavailable';
  }
};

/**
 * Sync user info to localStorage.sessionContext
 * CRITICAL: Ensures email is available for subsequent API calls and proactive token refresh
 * @param {object} userInfo - User information object
 */
export const syncSessionContextToStorage = (userInfo) => {
  if (typeof window === 'undefined' || !userInfo?.email) {
    // Missing window or email, skipping sync
    return;
  }

  try {
    const sessionData = {
      id: userInfo.id,
      email: userInfo.email,
      username: userInfo.username,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      phone: userInfo.phone,
      roles: userInfo.roles,
      isInternal: userInfo.isInternal,
      localeInfo: userInfo.localeInfo,
      userEmail: userInfo.email, // For backwards compatibility
      type: 'USER'
    };
    window.localStorage.setItem('sessionContext', JSON.stringify(sessionData));
    window.sessionStorage.setItem('sessionContext', JSON.stringify(sessionData));
  } catch (storageError) {
    // fail silently - session context sync is a nice-to-have for proactive refresh and API calls, but not critical to function
  }
};

export const isInternalUserAuthContext = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const sessionRaw = window.localStorage.getItem('sessionContext')
      || window.sessionStorage.getItem('sessionContext');

    if (!sessionRaw) {
      return false;
    }

    const parsed = JSON.parse(sessionRaw);
    return parsed?.isInternal === true || parsed?.userInfo?.isInternal === true;
  } catch (e) {
    return false;
  }
};

/**
 * Decode JWT token and extract claims
 * @param {string} token - JWT token
 * @returns {object|null} Decoded token claims or null if invalid
 */
export const decodeJWT = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    // Handle URL-safe base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const padded = base64 + '=='.substring(0, (4 - base64.length % 4) % 4);
    const decoded = atob(padded);
    const claims = JSON.parse(decoded);
    
    return claims;
  } catch (error) {
    return null;
  }
};

// Shared state: pending token refresh promise (prevents concurrent refresh attempts)
let pendingTokenRefresh = null;

const redirectToAsmReLogin = (reason = 'token_refresh_failed') => {
  if (typeof window === 'undefined') return;

  const currentPath = window.location?.pathname || '';
  if (!currentPath.includes('/auth/asmUser')) return;

  try {
    const now = Date.now();
    const lastRedirectAt = Number(window.sessionStorage.getItem('asm.relogin.lastRedirectAt') || 0);
    if (now - lastRedirectAt < 5000) {
      return;
    }
    window.sessionStorage.setItem('asm.relogin.lastRedirectAt', String(now));

    window.localStorage.removeItem(MSAL_CACHE);
    window.sessionStorage.removeItem(MSAL_CACHE);
    window.localStorage.removeItem('msal.account');
  } catch (storageError) {
    // fail silently - clearing stale auth cache is a nice-to-have but not critical
  }

  const redirectUrl = `/auth/employee?relogin=1&reason=${encodeURIComponent(reason)}`;
  // fail silently - redirecting ASM user to re-login page is a nice-to-have but not critical
  window.location.replace(redirectUrl);
};

// Helper to set pendingTokenRefresh (for testing)
export const setPendingTokenRefresh = (promise) => {
  pendingTokenRefresh = promise;
};

// Helper to get pendingTokenRefresh
export const getPendingTokenRefresh = () => pendingTokenRefresh;

/**
 * Parse ISO 8601 date string to milliseconds since epoch
 * @param {string} dateString - ISO 8601 date string
 * @returns {number} Milliseconds since epoch
 */
export const parseIso8601Date = (dateString) => {
  const d = dateString.split(/\D+/);
  return new Date(Date.UTC(d[0], d[1] - 1, d[2], d[3], d[4], d[5], d[6])).getTime();
};

/**
 * GraphQL mutation for auth callback
 */
export const AUTH_CALLBACK_MUTATION = `
  mutation MsalAuthCallback($code: String!, $isInternal: Boolean, $isLocal: Boolean, $source: String, $codeVerifier: String) {
    authCallback(code: $code, isInternal: $isInternal, isLocal: $isLocal, source: $source, codeVerifier: $codeVerifier) {
      wasSuccessful
    }
  }
`;

/**
 * Wait for global Auth instance to become available
 * @param {number} timeoutMs - Maximum wait time in milliseconds
 * @param {number} intervalMs - Polling interval in milliseconds
 * @returns {Promise<object|null>} Auth instance or null if timeout
 */
export const waitForAuthInstance = (timeoutMs = 3000, intervalMs = 100) => new Promise(resolve => {
  const start = Date.now();
  const tick = () => {
    const auth = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
    if (auth || Date.now() - start >= timeoutMs) {
      return resolve(auth || null);
    }
    return setTimeout(tick, intervalMs);
  };
  tick();
});

/**
 * Sync token with backend via direct GraphQL fetch
 * @param {string} accessToken - Access token to sync
 * @param {string} codeVerifier - Optional code verifier for PKCE validation
 * @returns {Promise<object>} Auth callback result
 */
export const syncTokenWithBackendDirect = async (accessToken, codeVerifier = null) => {
  const startedAt = Date.now();
  const graphqlEndpoint = process.env.GRAPHQL_ENDPOINT;
  if (!graphqlEndpoint) {
    throw new Error('GRAPHQL_ENDPOINT env var is missing - cannot sync token directly');
  }

  // NOTE: MSAL authentication uses Authorization: Bearer header (set by checkAccessToken link)
  // HttpOnly cookie is set by backend via Set-Cookie response header (not by JavaScript)
  // No need to manually set cookie here

  const payload = {
    query: AUTH_CALLBACK_MUTATION,
    variables: {
      code: accessToken,
      isInternal: true,
      isLocal: process.env.IS_LOCAL === 'true',
      source: 'castrolmarine',
      codeVerifier // ✅ CRITICAL: Include codeVerifier for PKCE validation
    }
  };

  const response = await fetch(graphqlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
      // ❌ NO Authorization header - token is sent via cookie
    },
    credentials: 'include', // ✅ CRITICAL: Send cookies with request
    body: JSON.stringify(payload)
  });

  const result = await response.json();

  if (!response.ok || result?.errors?.length) {
    const message = result?.errors?.map(e => e?.message).join('; ') || response.statusText;
    logFeErrorToServer({
      message: `Auth callback via fetch failed: ${message}`,
      stackTrace: undefined,
      component: 'applications-utils - syncTokenWithBackendDirect',
      type: 'error',
      statusCode: response.status || 500
    });
    throw new Error(`Auth callback via fetch failed: ${message}`);
  }

  const success = result?.data?.authCallback?.wasSuccessful;
  if (!success) {
    throw new Error('Auth callback via fetch did not succeed');
  }

  // fail silently - backend auth callback sync is a nice-to-have but not critical
  return result.data;
};

/**
 * Refresh token via MSAL and sync with backend
 * @param {object} msalInstance - MSAL instance
 * @param {Array<string>} scopes - AAD scopes
 * @returns {Promise<string|null>} New access token or null
 */
export const refreshTokenViaMSAL = async (msalInstance, scopes) => {
  // Get accounts
  // fail silently - token refresh via MSAL is a nice-to-have but not critical
  const resolvedMsalInstance = msalInstance || (typeof window !== 'undefined'
    ? (window.msalConfig?.instance || window.__AUTH_INSTANCE__?.msal?.instance)
    : null);

  if (!resolvedMsalInstance) {
    // fail silently - no MSAL instance available
    return null;
  }

  if (resolvedMsalInstance?.initialize && resolvedMsalInstance.initialized === false) {
    try {
      await resolvedMsalInstance.initialize();
      // fail silently - MSAL instance initialization is a nice-to-have but not critical
    } catch (initError) {
      // fail silently - MSAL instance initialization is a nice-to-have but not critical
    }
  }

  // Read session context from localStorage (where it's actually stored)
  let sessionEmail = null;
  if (typeof window !== 'undefined') {
    try {
      const sessionData = window.localStorage.getItem('sessionContext');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        sessionEmail = parsed.email || parsed.userEmail || parsed.username;
      }
    } catch (e) {
      // Error reading session context from localStorage
    }
  }

  const accounts = resolvedMsalInstance.getAllAccounts?.() || [];
  let activeAccount = resolvedMsalInstance.getActiveAccount?.() || null;

  if (!activeAccount && accounts.length > 0) {
    activeAccount = accounts.find(a =>
      sessionEmail && a?.username?.toLowerCase?.() === sessionEmail.toLowerCase()
    ) || accounts[0];

    if (activeAccount && resolvedMsalInstance.setActiveAccount) {
      resolvedMsalInstance.setActiveAccount(activeAccount);
      resolvedMsalInstance.enableAccountStorageEvents?.();
      // fail silently - MSAL active account set from cached accounts is a nice-to-have but not critical
    }
  }

  const resolveAccountFromHints = (usernameHint, savedAccount) => {
    if (!resolvedMsalInstance?.getAccount) {
      return null;
    }

    const filters = [
      savedAccount?.homeAccountId ? { homeAccountId: savedAccount.homeAccountId } : null,
      savedAccount?.localAccountId ? { localAccountId: savedAccount.localAccountId } : null,
      savedAccount?.username ? { username: savedAccount.username } : null,
      usernameHint ? { username: usernameHint } : null
    ].filter(Boolean);

    for (const filter of filters) {
      try {
        const found = resolvedMsalInstance.getAccount(filter);
        if (found) {
          return found;
        }
      } catch (accountError) {
        // getAccount lookup failed for filter
      }
    }

    return null;
  };
  
  // Fallback: If no accounts in MSAL, try to restore from localStorage
  if (!accounts || accounts.length === 0) {
    // CRITICAL: Try to restore MSAL account from localStorage
    if (typeof window !== 'undefined' && resolvedMsalInstance.setActiveAccount) {
      try {
        const savedAccountJson = window.localStorage.getItem('msal.account');
        if (savedAccountJson) {
          const savedAccount = JSON.parse(savedAccountJson);
          // fail silently - found saved MSAL account in localStorage is a nice-to-have but not critical

          if (!sessionEmail && savedAccount?.username) {
            sessionEmail = savedAccount.username;
            // fail silently - restored session email from saved MSAL account is a nice-to-have but not critical
          }

          const restoredAccount = resolveAccountFromHints(sessionEmail, savedAccount);
          if (restoredAccount) {
            resolvedMsalInstance.setActiveAccount(restoredAccount);
            // fail silently - restored MSAL account via getAccount filter is a nice-to-have but not critical
          }
          
          try {
            // Prefer explicit account to avoid no_account_error; fallback to loginHint
            const result = await resolvedMsalInstance.acquireTokenSilent({
              scopes,
              forceRefresh: true,
              ...(restoredAccount
                ? { account: restoredAccount }
                : { loginHint: savedAccount.username || sessionEmail })
            });
            
            // 🔧 NEW: Update session context with account info using utility function
            if (result?.account) {
              const userInfo = {
                id: result.account.homeAccountId || result.account.localAccountId,
                email: result.account.username || result.account.userPrincipalName,
                username: result.account.username,
                firstName: '',
                lastName: '',
                isInternal: true,
                roles: []
              };
              syncSessionContextToStorage(userInfo);
              
              // Also set as active account for future requests
              resolvedMsalInstance.setActiveAccount(result.account);
            }
            
            // Cache the new token
            if (typeof window !== 'undefined') {
              try {
                const expiresOnTime = typeof result.expiresOn === 'number' 
                  ? result.expiresOn 
                  : parseIso8601Date(new Date(result.expiresOn).toISOString());
                
                const msalTokenRefreshTime = expiresOnTime - (3 * 60 * 1000);
                
                const cacheEntry = {
                  expiresOn: expiresOnTime,
                  value: {
                    ...result,
                    expiresOn: expiresOnTime,
                    msalTokenRefreshTime
                  }
                };
                
                window.sessionStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
                window.localStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
              } catch (cacheError) {
                //Error updating cache:
              }
            }
            
            // Sync with backend to refresh HttpOnly session cookie
            try {
              // eslint-disable-next-line no-underscore-dangle
              const authB1 = (typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null)
                || await waitForAuthInstance(3000, 100);
              if (authB1?.syncMsalSession) {
                await authB1.syncMsalSession(result.accessToken, { updateUserInfo: true });
              } else {
                await syncTokenWithBackendDirect(result.accessToken, null);
              }
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('auth.session.headers.ready.ts', String(Date.now()));
                window.dispatchEvent(new Event('authSessionHeadersReady'));
              }
            } catch (syncError) {
              // Non-fatal: continue even if backend sync fails
            }

            // NOTE: do NOT reset Apollo cache here. tokenInjectionLink reads
            // MSAL_CACHE on every request so the new token is used automatically.
            // cache.reset() clears all component data causing a visible "page refresh"
            // effect on every proactive token renewal (every ~3 min before expiry).
            return result.accessToken;
          } catch (refreshError) {
            logFeErrorToServer({
              message: `MSAL token refresh from restored account failed: ${refreshError?.errorCode || refreshError?.error || refreshError?.message || 'unknown'}, subError: ${refreshError?.subError || refreshError?.suberror || 'N/A'}`,
              stackTrace: refreshError?.stack || refreshError?.error_description,
              component: 'applications-utils - refreshTokenViaMSAL',
              type: 'error',
              statusCode: 401
            });
            throwIfUnrecoverable(refreshError); // propagates invalid_grant immediately
            // Continue to fallback below
          }
        }
      } catch (restoreError) {
        // eslint-disable-next-line spaced-comment
        //Could not restore account from localStorage:
      }
    }
    
    if (!sessionEmail) {
      return null;
    }
    
    // Final fallback: Try acquireTokenSilent without account
    try {
      const fallbackAccount = resolveAccountFromHints(sessionEmail, null);
      if (fallbackAccount && resolvedMsalInstance.setActiveAccount) {
        resolvedMsalInstance.setActiveAccount(fallbackAccount);
      }
      
      // Try acquireTokenSilent with loginHint to help MSAL find the cached account
      const result = await resolvedMsalInstance.acquireTokenSilent({
        scopes,
        forceRefresh: true,
        ...(fallbackAccount
          ? { account: fallbackAccount }
          : { loginHint: sessionEmail })
      });
      
      // 🔧 NEW: Extract user info from result if available (for when MSAL finds an account)
      if (result?.account) {
        // Set as active account for future requests
        if (resolvedMsalInstance.setActiveAccount) {
          resolvedMsalInstance.setActiveAccount(result.account);
        }
        
        const userInfo = {
          id: result.account.homeAccountId || result.account.localAccountId,
          email: result.account.username || result.account.userPrincipalName,
          username: result.account.username,
          firstName: '',
          lastName: '',
          isInternal: true,
          roles: []
        };
        syncSessionContextToStorage(userInfo);
      }
      
      // STEP 1: Update cache FIRST
      if (typeof window !== 'undefined') {
        try {
          const expiresOnTime = typeof result.expiresOn === 'number' 
            ? result.expiresOn 
            : parseIso8601Date(new Date(result.expiresOn).toISOString());
          
          const msalTokenRefreshTime = expiresOnTime - (3 * 60 * 1000);
          
          const cacheEntry = {
            expiresOn: expiresOnTime,
            value: {
              ...result,
              expiresOn: expiresOnTime,
              msalTokenRefreshTime
            }
          };
          
          window.sessionStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
          window.localStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
        } catch (cacheError) {
          //Error updating cache (fallback):
        }
      }
      
      // STEP 2: Sync with backend to refresh HttpOnly session cookie
      try {
        // eslint-disable-next-line no-underscore-dangle
        const authFb = (typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null)
          || await waitForAuthInstance(3000, 100);
        if (authFb?.syncMsalSession) {
          await authFb.syncMsalSession(result.accessToken, { updateUserInfo: true });
        } else {
          await syncTokenWithBackendDirect(result.accessToken, null);
        }
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem('auth.session.headers.ready.ts', String(Date.now()));
          window.dispatchEvent(new Event('authSessionHeadersReady'));
        }
      } catch (syncError) {
        // Non-fatal: continue even if backend sync fails
      }

      // NOTE: do NOT reset Apollo cache here. tokenInjectionLink picks up the
      // new token from MSAL_CACHE automatically on the next request.
      return result.accessToken;
    } catch (fallbackError) {

      if (fallbackError?.errorCode === 'no_account_error' && resolvedMsalInstance?.acquireTokenRedirect && sessionEmail && isInternalUserAuthContext()) {
        try {
          const REDIRECT_RECOVERY = 'msal.redirectRecoveryAttempted';
          const currentPath = window.location?.pathname || '';
          const hasAttemptedRedirectRecovery = window.sessionStorage.getItem(REDIRECT_RECOVERY) === 'true';

          if (!hasAttemptedRedirectRecovery && !currentPath.includes('/auth/employee')) {
            const interactionLock = Auth.getMsalInteractionLock();
            if (interactionLock) {
              return null;
            }

            window.sessionStorage.setItem(REDIRECT_RECOVERY, 'true');
            Auth.setMsalInteractionLock('applications-utils.acquireTokenRedirect');
            await resolvedMsalInstance.acquireTokenRedirect({
              scopes,
              loginHint: sessionEmail,
              redirectStartPage: window.location.href
            });
          }
        } catch (redirectError) {
          if (String(redirectError?.message || '').includes('state_not_found')) {
            Auth.clearMsalInteractionLock();
          }
        }
      }

      logFeErrorToServer({
        message: `MSAL silent refresh fallback failed: ${fallbackError?.errorCode || fallbackError?.error || fallbackError?.message || 'unknown'}, subError: ${fallbackError?.subError || fallbackError?.suberror || 'N/A'}`,
        stackTrace: fallbackError?.stack || fallbackError?.error_description,
        component: 'applications-utils - refreshTokenViaMSAL',
        type: 'error',
        statusCode: 401
      });
      throwIfUnrecoverable(fallbackError); // propagates invalid_grant immediately
      redirectToAsmReLogin(fallbackError?.errorCode || 'silent_refresh_failed');
      return null;
    }
  }
  
  const account = resolvedMsalInstance.getActiveAccount?.() || accounts[0];
  
  // Update session context with user email from account if not already set
  if (typeof window !== 'undefined' && account) {
    try {
      const sessionData = window.localStorage.getItem('sessionContext') 
        ? JSON.parse(window.localStorage.getItem('sessionContext'))
        : {};
      
      // Only update if email is missing
      if (!sessionData.email || !sessionData.id) {
        sessionData.email = account.username || account.userPrincipalName || sessionData.email;
        sessionData.userEmail = sessionData.email;
        sessionData.id = account.homeAccountId || account.localAccountId || sessionData.id;
        window.localStorage.setItem('sessionContext', JSON.stringify(sessionData));
      }
    } catch (sessionError) {
      //Could not update session context from account:
    }
  }
  
  // Refresh token using configured scopes
  let result;
  try {
    result = await resolvedMsalInstance.acquireTokenSilent({
      scopes,
      account,
      // 🔧 FIX: Use forceRefresh: false so MSAL can return its cached token when
      // still valid (within tokenRenewalOffsetSeconds: 120s). Previously forceRefresh: true
      // forced a network round-trip on every Apollo request in the last 5 minutes, and
      // any transient failure cascaded into "redirect to SSO" premature page reloads.
      // MSAL will naturally renew via its own offset when the token is close to expiry.
      forceRefresh: false
    });
  } catch (error) {
    
    const isInteractionInProgress = String(error?.errorCode || '').includes('interaction_in_progress')
      || String(error?.message || '').includes('interaction_in_progress')
      || String(error?.message || '').includes('msal_interaction_in_progress');
    const isStateNotFound = String(error?.errorCode || '').includes('state_not_found')
      || String(error?.message || '').includes('state_not_found');

    if (isInteractionInProgress || isStateNotFound) {
      if (isStateNotFound) {
        Auth.clearMsalInteractionLock();
      }

      try {
        // eslint-disable-next-line no-underscore-dangle
        const redirectSyncPromise = typeof window !== 'undefined' ? window.__MSAL_REDIRECT_SYNC_PROMISE__ : null;
        if (redirectSyncPromise && typeof redirectSyncPromise.then === 'function') {
          await Promise.race([
            redirectSyncPromise,
            // eslint-disable-next-line no-promise-executor-return
            new Promise(resolve => setTimeout(resolve, 5000))
          ]);
        } else {
          // eslint-disable-next-line no-promise-executor-return
          await new Promise(resolve => setTimeout(resolve, 700));
        }

        const retryAccount = resolvedMsalInstance.getActiveAccount?.() || (resolvedMsalInstance.getAllAccounts?.() || [])[0] || account;
        if (retryAccount && resolvedMsalInstance.setActiveAccount) {
          resolvedMsalInstance.setActiveAccount(retryAccount);
        }

        result = await resolvedMsalInstance.acquireTokenSilent({
          scopes,
          account: retryAccount,
          forceRefresh: false
        });
      } catch (retryError) {
        logFeErrorToServer({
          message: `MSAL acquireTokenSilent retry failed after interaction_in_progress: ${retryError?.errorCode || retryError?.error || retryError?.message || 'unknown'}, subError: ${retryError?.subError || retryError?.suberror || 'N/A'}`,
          stackTrace: retryError?.stack || retryError?.error_description,
          component: 'applications-utils - refreshTokenViaMSAL',
          type: 'error',
          statusCode: 401
        });
        throwIfUnrecoverable(retryError);
        return null;
      }
    } else {
      logFeErrorToServer({
        message: `MSAL acquireTokenSilent failed (invalid_grant/expired): ${error?.errorCode || error?.error || error?.message || 'unknown'}, subError: ${error?.subError || error?.suberror || 'N/A'}`,
        stackTrace: error?.stack || error?.error_description,
        component: 'applications-utils - refreshTokenViaMSAL',
        type: 'error',
        statusCode: 401
      });
      throwIfUnrecoverable(error);
      return null;
    }
  }
  
  // STEP 1: Update cache FIRST (critical)
  if (typeof window !== 'undefined') {
    try {
      const expiresOnTime = typeof result.expiresOn === 'number' 
        ? result.expiresOn 
        : parseIso8601Date(new Date(result.expiresOn).toISOString());
      
      const msalTokenRefreshTime = expiresOnTime - (3 * 60 * 1000); // 3 minute buffer
      
      const cacheEntry = {
        expiresOn: expiresOnTime,
        value: {
          ...result,
          expiresOn: expiresOnTime,
          msalTokenRefreshTime
        }
      };
      
      // Store in both sessionStorage and localStorage
      window.sessionStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
      window.localStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
    } catch (cacheError) {
      // Continue anyway - token is still valid even if cache failed
    }
  }
  
  // STEP 2: Sync with backend (non-blocking - doesn't stop token refresh on failure)
  let auth = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;

  if (!auth) {
    auth = await waitForAuthInstance(5000, 100);
  }
  
  // CRITICAL: Ensure backend is synced with new token
  const retrySyncBackend = async () => {
    const maxSyncAttempts = 3;
    const lastError = null;
    
    for (let attempt = 1; attempt <= maxSyncAttempts; attempt += 1) {
      try {
        if (auth?.syncMsalSession) {
          // eslint-disable-next-line no-await-in-loop
          await auth.syncMsalSession(result.accessToken, { updateUserInfo: true });
          return true;
        }
        if (auth?.gqlAuthCallbackMSAL && auth?.gqlUserInfo && auth?.setUserInfo) {
          // eslint-disable-next-line no-await-in-loop
          await auth.gqlAuthCallbackMSAL(result.accessToken);
          
          // Also refresh user info
          try {
            // eslint-disable-next-line no-await-in-loop
            const userInfo = await auth.gqlUserInfo(true);
            auth.setUserInfo(userInfo);
          } catch (userInfoError) {
            // Non-fatal: backend is synced, just couldn't update local user info
          }
          return true;
        }

        // Get codeVerifier from session context for PKCE validation
        let codeVerifierFromSession = null;
        try {
          if (typeof window !== 'undefined') {
            const sessionData = window.localStorage.getItem('sessionContext');
            if (sessionData) {
              const parsed = JSON.parse(sessionData);
              codeVerifierFromSession = parsed.codeVerifier || null;
            }
          }
        } catch (e) {
          // Continue without codeVerifier
        }
        // eslint-disable-next-line no-await-in-loop
        await syncTokenWithBackendDirect(result.accessToken, codeVerifierFromSession);
        return true;
      } catch (syncError) {
        if (attempt < maxSyncAttempts) {
          // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
    return false;
  };

  // STEP 2b: Actually call the backend sync (was defined above but never invoked)
  await retrySyncBackend();

  // After a successful backend sync, fire the authSessionHeadersReady event so
  // the authRouteStabilizationLink on /auth/asmUser lets queries through immediately
  // instead of waiting for the 2500ms fallback timeout.
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem('auth.session.headers.ready.ts', String(Date.now()));
      window.dispatchEvent(new Event('authSessionHeadersReady'));
    } catch (e) {
      // ignore storage/dispatch failure
    }
  }

  // NOTE: do NOT reset Apollo cache here. tokenInjectionLink reads MSAL_CACHE
  // on every request so the new token is injected automatically without clearing
  // component data. cache.reset() on every proactive refresh (every ~3 min before
  // expiry) caused all page data to disappear and reload — visible "page refresh".

  return result.accessToken;
};

/**
 * Ensure Auth instance is ready with a valid token
 * @param {function} getTokenExpirationInfo - Function to get token expiration info
 * @param {function} showOverlay - Function to show refresh overlay
 * @param {function} hideOverlay - Function to hide refresh overlay
 * @returns {Promise<void>}
 */
export const ensureAuthReady = async (getTokenExpirationInfo, showOverlay, hideOverlay) => {
  // If there's already a pending refresh, wait for it
  if (pendingTokenRefresh) {
    try {
      await pendingTokenRefresh;
    } catch (e) {
      // Refresh failed, but continue to check token state
    }
  }

  // Check token state
  const tokenInfo = getTokenExpirationInfo();
  
  // If token is valid and not expiring soon, we're good
  const TWO_MINUTES_MS = 2 * 60 * 1000;
  if (tokenInfo && !tokenInfo.hasExpired && tokenInfo.msUntilExpiry > TWO_MINUTES_MS) {
    return; // Token is good, proceed without Auth instance
  }

  // Token is missing, expired, or expiring soon - need to refresh
  // But ONLY if Auth instance is available (don't wait for it)
  
  let auth = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
  
  if (!auth) {
    // Try once more with a short wait to avoid immediate UNAUTH in the first requests
    auth = await waitForAuthInstance(2000, 100);
  }

  if (!auth) {
    return; // Proceed without refresh attempt
  }

  // Auth IS available - do proactive refresh if needed
  
  showOverlay();
  
  try {
    if (!pendingTokenRefresh) {
      pendingTokenRefresh = (async () => {
        try {
          if (tokenInfo?.hasExpired || !tokenInfo) {
            await auth.getMsalAccessToken(true); // Force refresh
          } else {
            await auth.refreshMsalToken();
          }
          
          // Verify token is now valid
          const refreshedTokenInfo = auth?.getTokenExpirationInfo?.();
          if (!refreshedTokenInfo || refreshedTokenInfo.hasExpired) {
            throw new Error('Token still invalid after refresh');
          }
        } finally {
          pendingTokenRefresh = null;
        }
      })();
    }

    await pendingTokenRefresh;
  } catch (error) {
    throw error;
  } finally {
    hideOverlay();
  }
};

/**
 * Attempt silent token refresh via MSAL
 * @param {object} msalInstance - MSAL instance
 * @param {Array<string>} scopes - AAD scopes
 * @returns {Promise<string|null>} New access token or null if refresh fails
 */
export const attemptSilentTokenRefresh = async (msalInstance, scopes) => {
  if (!msalInstance) {
    return null;
  }

  try {
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }

    const account = msalInstance.getActiveAccount?.() || accounts[0];
    
    // Attempt silent refresh without showing UI
    const result = await msalInstance.acquireTokenSilent({
      scopes,
      account,
      forceRefresh: true
    });
    
    // Update cache FIRST
    if (typeof window !== 'undefined') {
      try {
        const expiresOnTime = typeof result.expiresOn === 'number' 
          ? result.expiresOn 
          : new Date(result.expiresOn).getTime();
        
        const cacheEntry = {
          expiresOn: expiresOnTime,
          value: {
            ...result,
            expiresOn: expiresOnTime,
            msalTokenRefreshTime: expiresOnTime - (3 * 60 * 1000)
          }
        };
        
        window.sessionStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
        window.localStorage.setItem(MSAL_CACHE, JSON.stringify(cacheEntry));
      } catch (cacheError) {
        //Could not update cache:
      }
    }
    
    // Sync with backend (non-blocking)
    try {
      // Get codeVerifier from session context for PKCE validation
      let codeVerifierFromSessionSilent = null;
      try {
        if (typeof window !== 'undefined') {
          const sessionData = window.localStorage.getItem('sessionContext');
          if (sessionData) {
            const parsed = JSON.parse(sessionData);
            codeVerifierFromSessionSilent = parsed.codeVerifier || null;
          }
        }
      } catch (e) {
        // Continue without codeVerifier
      }
      await syncTokenWithBackendDirect(result.accessToken, codeVerifierFromSessionSilent);
      // Signal to authRouteStabilizationLink that session headers are ready
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(AUTH_SESSION_HEADERS_READY_TS, String(Date.now()));
        window.dispatchEvent(new Event('authSessionHeadersReady'));
      }
    } catch (syncError) {
      // Non-blocking: token is still valid locally
    }

    // NOTE: do NOT reset Apollo cache here. tokenInjectionLink picks up the new
    // token from MSAL_CACHE automatically on every subsequent request.

    return result.accessToken;
  } catch (error) {
    // Detect unrecoverable errors: SPA refresh token expired (AADSTS700084/invalid_grant)
    // or any interaction-required error. Silent refresh will NEVER succeed for these —
    // re-throw with a marker so callers can redirect to SSO immediately instead of retrying.
    const isUnrecoverable = isMsalTokenExpiredError(error);
    if (isUnrecoverable) {
      logFeErrorToServer({
        message: `Unrecoverable MSAL token error (invalid_grant/expired): ${error?.errorCode || error?.error || error?.message || 'unknown'}, subError: ${error?.subError || error?.suberror || 'N/A'}`,
        stackTrace: error?.stack || error?.error_description,
        component: 'applications-utils - attemptSilentTokenRefresh',
        type: 'error',
        statusCode: 401
      });
      const unrecoverableError = new Error(`unrecoverable_token_error: ${error.message}`);
      unrecoverableError.isUnrecoverable = true;
      unrecoverableError.originalErrorCode = error?.errorCode || error?.error || '';
      throw unrecoverableError;
    }
    logFeErrorToServer({
      message: `MSAL silent token refresh failed: ${error?.errorCode || error?.error || error?.message || 'unknown'}, subError: ${error?.subError || error?.suberror || 'N/A'}`,
      stackTrace: error?.stack || error?.error_description,
      component: 'applications-utils - attemptSilentTokenRefresh',
      type: 'error',
      statusCode: error?.status || 500
    });
    return null;
  }
};

/**
 * Handle Apollo error with retry logic and silent refresh
 * @param {object} errorContext - Error context from Apollo
 * @param {function} refreshToken - Function to refresh token
 * @param {function} showOverlay - Function to show refresh overlay
 * @param {function} hideOverlay - Function to hide refresh overlay
 * @returns {Observable|undefined} Observable for retry or undefined
 */
export const handleApolloError = ({ 
  graphQLErrors, 
  networkError, 
  operation, 
  forward,
  Observable 
}, refreshToken, showOverlay, hideOverlay) => {
  const authTraceId = getAuthTraceId();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAuthRoute = currentPath.includes('/auth/');
  const isAuthStabilizationRoute = currentPath.includes('/auth/employee') || currentPath.includes('/auth/asmUser');

  if (isPublicRoute()) {
    return undefined;
  }

  const isInternalContext = isInternalUserAuthContext();

  const isUnauth = Array.isArray(graphQLErrors) && graphQLErrors.some(e => 
    e?.extensions?.code === 'UNAUTHENTICATED' || e?.message?.toLowerCase?.().includes('unauth')
  );
  const is401 = networkError && (networkError.statusCode === 401 || networkError.response?.status === 401);

  if (!isInternalContext && (isUnauth || is401)) {
    logFeErrorToServer({
      message: `External user UNAUTHENTICATED error. Operation: ${operation?.operationName || 'unknown'}`,
      stackTrace: graphQLErrors?.map(e => e?.message).join('; '),
      component: 'applications-utils - handleApolloError',
      type: 'error',
      statusCode: 401
    });
    return undefined;
  }
  
  if (isUnauth || is401) {
    logFeErrorToServer({
      message: `Internal user UNAUTHENTICATED/401 error. Operation: ${operation?.operationName || 'unknown'}, isAuthRoute: ${isAuthRoute}`,
      stackTrace: graphQLErrors?.map(e => e?.message).join('; ') || networkError?.message,
      component: 'applications-utils - handleApolloError',
      type: 'error',
      statusCode: 401
    });
    // Check if we've already retried this operation
    const context = operation.getContext();
    const retryCount = context.retryCount || 0;
    const hasRecentSessionHeadersReady = (() => {
      if (typeof window === 'undefined') return false;
      try {
        const ts = Number(window.sessionStorage.getItem(AUTH_SESSION_HEADERS_READY_TS) || 0);
        return ts > 0 && (Date.now() - ts) < 30000;
      } catch (e) {
        return false;
      }
    })();

    // During auth route stabilization, avoid immediate token-refresh loops.
    // Wait for redirect sync (if present) and retry operation once first.
    // eslint-disable-next-line no-underscore-dangle
    const redirectSyncPromise = typeof window !== 'undefined' ? window.__MSAL_REDIRECT_SYNC_PROMISE__ : null;
    if (isAuthStabilizationRoute && retryCount < 3) {
      operation.setContext({
        ...context,
        retryCount: retryCount + 1
      });

      return new Observable((subscriber) => {
        // If the cached token is already expired, proactively kick off a refresh
        // instead of waiting passively for authSessionHeadersReady. Without this,
        // the stabilization branch just waits and retries with the same expired token.
        // refreshToken() → refreshTokenViaMSAL → retrySyncBackend → fires authSessionHeadersReady
        const isCachedTokenExpired = (() => {
          if (typeof window === 'undefined') return false;
          try {
            const raw = window.sessionStorage.getItem(MSAL_CACHE) || window.localStorage.getItem(MSAL_CACHE);
            if (!raw) return true;
            const parsed = JSON.parse(raw);
            const exp = Number(parsed.expiresOn || parsed.value?.expiresOn || 0);
            return exp === 0 || Date.now() >= exp;
          } catch (e) {
            return false;
          }
        })();

        if (isCachedTokenExpired && !pendingTokenRefresh) {
          pendingTokenRefresh = refreshToken()
            .catch((err) => {
              if (err?.isUnrecoverable || isMsalTokenExpiredError(err)) {
                clearMsalStorageAndRedirect();
              }
              return null;
            })
            // eslint-disable-next-line no-promise-executor-return
            .finally(() => setTimeout(() => { pendingTokenRefresh = null; }, 3000));
        }

        const waitForSessionHeadersReady = () => {
          if (typeof window === 'undefined' || hasRecentSessionHeadersReady || !currentPath.includes('/auth/asmUser')) {
            return Promise.resolve();
          }

          return new Promise(resolve => {
            let done = false;
            let onReady;
            const finish = () => {
              if (done) return;
              done = true;
              if (onReady) {
                window.removeEventListener('authSessionHeadersReady', onReady);
              }
              resolve();
            };
            onReady = () => finish();

            window.addEventListener('authSessionHeadersReady', onReady, { once: true });
            // Extended from 2000ms: full MSAL refresh + authCallback backend sync
            // can take up to ~5s. Short timeout caused retries with still-expired token.
            setTimeout(finish, 6000);
          });
        };

        const stabilizationPromise = redirectSyncPromise && typeof redirectSyncPromise.then === 'function'
          ? Promise.race([
            redirectSyncPromise,
            // eslint-disable-next-line no-promise-executor-return
            new Promise(resolve => setTimeout(resolve, 3000))
          ])
          // eslint-disable-next-line no-promise-executor-return
          : new Promise(resolve => setTimeout(resolve, 600));

        stabilizationPromise
          .then(() => waitForSessionHeadersReady())
          .then(() => {
            forward(operation).subscribe({
              next: (value) => subscriber.next(value),
              error: (err) => subscriber.error(err),
              complete: () => subscriber.complete()
            });
          })
          .catch((err) => {
            subscriber.error(err);
          });
      });
    }
    
    const maxRetries = isAuthRoute ? 3 : 5;

    if (retryCount < maxRetries) {  
      // Mark this as a retry
      operation.setContext({
        ...context,
        retryCount: retryCount + 1
      });
      
      // Refresh token and retry the request
      if (!pendingTokenRefresh) {
        pendingTokenRefresh = (async () => {
          let caughtError = null;
          try {
            // Show overlay only if we have one
            if (showOverlay) showOverlay();
            
            const newToken = await refreshToken();
            if (newToken) {
              return newToken;
            } 
            throw new Error('Token refresh returned no token');
          } catch (e) {
            caughtError = e;
            const isUnrecoverableRefreshError =
              e?.isUnrecoverable === true || isMsalTokenExpiredError(e);

            if (isInternalContext && typeof window !== 'undefined') {
              const shouldSkipModal = currentPath.includes('/auth/employee') || currentPath.includes('/auth/asmUser');

              if (isUnrecoverableRefreshError) {
                Auth.clearMsalInteractionLock();
                clearMsalStorageAndRedirect();
              } else if (shouldSkipModal) {
                const isCachedTokenStillValid = (() => {
                  try {
                    const raw = window.sessionStorage.getItem(MSAL_CACHE) || window.localStorage.getItem(MSAL_CACHE);
                    if (!raw) return false;
                    const parsed = JSON.parse(raw);
                    const exp = Number(parsed.expiresOn || parsed?.value?.expiresOn || 0);
                    return exp > Date.now() + 30000; // valid for at least 30 more seconds
                  } catch (cacheReadErr) {
                    return false;
                  }
                })();
                if (!isCachedTokenStillValid) {
                  redirectToAsmReLogin('apollo_refresh_failed');
                }
              } else if (window.__SESSION_TIMEOUT_MANAGER__) {
                try {
                  window.__SESSION_TIMEOUT_MANAGER__.updateState?.(true);
                } catch (modalError) {
                  //Failed to show session timeout modal:
                }
              }
            }
            
            throw e; // Propagate the error
          } finally {
            if (hideOverlay) hideOverlay();
            // For unrecoverable errors clear immediately (page is being redirected anyway);
            // for recoverable failures use a short delay to debounce concurrent retries.
            const clearDelay = (caughtError?.isUnrecoverable ||
              String(caughtError?.message || '').includes('unrecoverable_token_error')) ? 0 : 3000;
            setTimeout(() => { pendingTokenRefresh = null; }, clearDelay);
          }
        })();
      } else {
        //Token refresh already in progress - joining existing refresh
      }
      
      // Return an Observable that waits for token refresh, then forwards
      return new Observable((subscriber) => {
        pendingTokenRefresh.then(
          () => {
            forward(operation).subscribe({
              next: (value) => subscriber.next(value),
              error: (err) => subscriber.error(err),
              complete: () => subscriber.complete()
            });
          },
          (err) => subscriber.error(err)
        );
      });
    }
    
    if (isAnalyticsConsented()) {
      trackEvent(TRACKING_EVENTS.APOLLO_ERROR_EXHAUSTED, getErrorContext(
        'unauthenticated_max_retries',
        graphQLErrors?.[0]?.message || 'UNAUTHENTICATED error - max retries reached',
        {
          retry_count: retryCount,
          operation_name: operation?.operationName || 'unknown'
        }
      ));
    }
    
    // Trigger SessionTimeoutManager modal instead of redirecting to error page
    if (isInternalContext && typeof window !== 'undefined' && window.__SESSION_TIMEOUT_MANAGER__) {
      // Do not show modal on /auth/employee or /auth/asmUser pages
      const shouldSkipModal = currentPath.includes('/auth/employee') || currentPath.includes('/auth/asmUser');
      
      if (!shouldSkipModal) {
        try {
          window.__SESSION_TIMEOUT_MANAGER__.updateState?.(true);
        } catch (modalError) {
          //Failed to show session timeout modal:
        }
      }
    }
    
    const auth = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
    
    if (auth?.refreshMsalToken) {
      // Try silent refresh one more time
      return new Observable((subscriber) => {
        auth.refreshMsalToken()
          .then(() => {
            forward(operation).subscribe({
              next: (value) => subscriber.next(value),
              error: (err) => subscriber.error(err),
              complete: () => subscriber.complete()
            });
          })
          .catch((refreshError) => {
            subscriber.error(refreshError);
          });
      });
    }
  }
  
  return undefined;
};

/**
 * Check if baseStore has changed on page load
 * If baseStore has changed, trigger cart refresh/reset and clear Apollo cache
 * @returns {Promise<boolean>} true if baseStore changed, false otherwise
 */
export const checkAndHandleBaseStoreChange = async () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Get previous baseStore from sessionStorage (key format: 'baseStore-previous')
    const previousBaseStore = window.sessionStorage.getItem('baseStore-previous');
    
    // Get current baseStore from session context
    let currentSessionData = null;
    try {
      const sessionDataStr = window.localStorage.getItem('sessionContext');
      if (sessionDataStr) {
        currentSessionData = JSON.parse(sessionDataStr);
      }
    } catch (e) {
      //Could not parse current session data
    }

    const currentBaseStore = currentSessionData?.baseStore || currentSessionData?.country || 'uk';

    // Check if baseStore has changed
    if (previousBaseStore && previousBaseStore !== currentBaseStore) {
      // 1. Clear Apollo cache for old baseStore context
      if (typeof window !== 'undefined' && window.__APOLLO_CLIENT__) {
        try {
          await window.__APOLLO_CLIENT__.cache.reset();
        } catch (apolloError) {
          //Could not reset Apollo cache on baseStore change:
        }
      }

      // 2. Dispatch cart refresh event
      if (typeof window !== 'undefined') {
        // Trigger cartRefreshNeeded event for Cart component
        window.dispatchEvent(new CustomEvent('cartRefreshNeeded', {
          detail: {
            reason: 'baseStore-changed',
            previousBaseStore,
            currentBaseStore,
            timestamp: Date.now()
          }
        }));

        // 3. Also trigger a global page refresh event that components can listen to
        window.dispatchEvent(new CustomEvent('baseStoreChanged', {
          detail: {
            previousBaseStore,
            currentBaseStore,
            timestamp: Date.now()
          }
        }));
      }

      // 4. Store current baseStore as the new "previous" for next page load
      window.sessionStorage.setItem('baseStore-previous', currentBaseStore);

      return true; // baseStore changed
    } if (!previousBaseStore) {
      // First page load - store the current baseStore
      window.sessionStorage.setItem('baseStore-previous', currentBaseStore);
      return false; // No change on first load
    }

    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Initialize baseStore change detection on page load
 * Should be called early in app initialization
 * @returns {void}
 */
export const initializeBaseStoreChangeDetection = () => {
  if (typeof window === 'undefined') {
    return;
  }
  
  // Use a small delay to ensure session context is populated
  setTimeout(() => {
    checkAndHandleBaseStoreChange().catch(error => {
      //Failed to check baseStore changes:
    });
  }, 500);

  // Also listen for session context updates to detect real-time baseStore changes
  const handleSessionTokenUpdate = () => {
    checkAndHandleBaseStoreChange().catch(error => {
      //Failed to check baseStore on token update:
    });
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('sessionTokenUpdated', handleSessionTokenUpdate);
    
    // Cleanup function for testing/unmount
    if (!window.__baseStoreChangeDetectionCleanup) {
      window.__baseStoreChangeDetectionCleanup = () => {
        window.removeEventListener('sessionTokenUpdated', handleSessionTokenUpdate);
      };
    }
  }
};
